import Bowser from 'bowser';

import { adviceRooms, guests } from 'api';
import { showApiErrorToast, showErrorToast } from 'helpers/toasts';
import {
  concatUint8Arrays,
  formatAudioMessage,
  handleTranscribeMessage,
  SAMPLE_RATE,
} from 'helpers/transcriptionAudioStream';

import type { AWSTranscribeHandlerProps, TranscriptionHandler } from './types';

let CACHED_WEBSOCKET_URL = '';
const MAX_QUEUE = 50;

const browser = Bowser.getParser(window.navigator.userAgent);
const isFirefox = browser.getBrowserName() === 'Firefox';

export default function HandleAWSTranscribe({
  adviceRoomId,
  authToken,
  sendToServer,
}: AWSTranscribeHandlerProps): TranscriptionHandler {
  let socket: WebSocket | null = null;
  let source: MediaStreamAudioSourceNode | undefined;
  let mediaStream: MediaStream | null = null;
  let worklet: AudioWorkletNode | null = null;
  let audioContext: AudioContext | null = null;
  let audioBuffer: Uint8Array[] = [];

  let isTranscriptionActive = false;
  let currentDeviceId: undefined | string;

  let healthCheckInterval: any;
  let refetchInterval: any;

  async function fetchWebsocketURL() {
    let promise: Promise<any> | null = null;
    if (authToken) {
      promise = guests().clientMeetingRooms().getWebsocketURL(authToken);
    } else if (adviceRoomId) {
      promise = adviceRooms(adviceRoomId).transcriptions().getWebsocketURL();
    }

    promise
      // @ts-ignore
      ?.then(({ data }) => {
        CACHED_WEBSOCKET_URL = data.data.attributes.url;
      })
      .catch(err => {
        showApiErrorToast(err);
      });

    return CACHED_WEBSOCKET_URL;
  }

  const getCachedWebsocketURL = () => {
    fetchWebsocketURL();

    const url = CACHED_WEBSOCKET_URL;
    CACHED_WEBSOCKET_URL = '';

    return url;
  };

  const connectWebsocket = async () => {
    if (socket && socket.readyState <= socket.OPEN) return Promise.resolve();

    return new Promise((resolve, reject) => {
      try {
        const url = getCachedWebsocketURL();

        if (!url) {
          reject(new Error('No WebSocket URL available'));
          return;
        }
        socket = new WebSocket(url);
        socket.binaryType = 'arraybuffer';

        socket.onmessage = message => {
          if (!(message.data instanceof ArrayBuffer)) return;
          handleTranscribeMessage(message.data, sendToServer);
        };

        socket.onopen = () => resolve('Connected');
        socket.onerror = err => reject(err);
      } catch (err) {
        reject(err);
      }
    });
  };

  const sendAudioBufferToAWSTranscribe = async () => {
    if (audioBuffer.length === 0) return;
    if (socket?.readyState !== socket?.OPEN) return;
    if (socket && socket?.bufferedAmount > 512_000) return;

    const maxChunk = 4800;
    const buffer = concatUint8Arrays(audioBuffer);

    audioBuffer = [];

    try {
      const chunks: Uint8Array[] = [];

      for (let i = 0; i < buffer.length; i += maxChunk) {
        const chunk = buffer.subarray(i, Math.min(i + maxChunk, buffer.length));
        chunks.push(chunk);
      }

      chunks.forEach(chunk => {
        if (!socket || socket.readyState !== socket.OPEN) {
          return;
        }

        const message = formatAudioMessage(chunk);

        socket?.send(message);
      });
    } catch (error) {
      showApiErrorToast(error);
      audioBuffer = [];
    }
  };

  const handleWorkletMessage = (event: any) => {
    if (isTranscriptionActive) {
      audioBuffer.push(new Uint8Array(event.data));
    }

    if (audioBuffer.length >= MAX_QUEUE) {
      audioBuffer.shift();
    }

    if (audioBuffer.length >= 4) {
      sendAudioBufferToAWSTranscribe();
    }
  };

  const handleDeviceChange = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    changeAudioDevice(currentDeviceId);
  };

  const cleanUpTranscription = async () => {
    try {
      clearInterval(refetchInterval);
      clearInterval(healthCheckInterval);

      if (socket && socket.readyState <= socket.OPEN) socket?.close();

      worklet?.port.removeEventListener('message', handleWorkletMessage);
      worklet?.port.close();
      worklet?.disconnect();

      source?.disconnect();
      mediaStream?.getTracks()?.forEach(track => {
        // eslint-disable-next-line no-param-reassign
        (track as any).onended = null;
        track.stop();
      });
      await audioContext?.close();

      audioContext = null;
      socket = null;
      worklet = null;
      mediaStream = null;
    } catch (err) {
      window.Rollbar?.error('Error cleaning up transcription handler', { adviceRoomId }, err);
    }
  };

  const checkHealth = () => {
    if (!isTranscriptionActive) return;

    if (audioContext?.state !== 'running') {
      audioContext?.resume();
    }

    if (socket && socket.readyState > socket.OPEN) {
      connectWebsocket();
    }
  };

  const initTranscribeHandler = async () => {
    fetchWebsocketURL();

    refetchInterval = setInterval(fetchWebsocketURL, 1000 * 60 * 4);
    healthCheckInterval = setInterval(checkHealth, 1000);
  };

  const stopTranscription = async () => {
    clearInterval(healthCheckInterval);
    sendAudioBufferToAWSTranscribe();
    if (socket && socket.readyState === socket.OPEN) {
      const message = formatAudioMessage(new Uint8Array(0));
      socket?.send(message);
    }

    await new Promise(r => setTimeout(r, 1000));

    worklet?.port.removeEventListener('message', handleWorkletMessage);
    worklet?.port.close();
    worklet?.disconnect();

    source?.disconnect();
    mediaStream?.getTracks()?.forEach(track => {
      // eslint-disable-next-line no-param-reassign
      (track as any).onended = null;
      track.stop();
    });
    await audioContext?.close();

    if (socket && socket.readyState <= socket.OPEN) socket?.close();

    audioContext = null;
    socket = null;
    worklet = null;
    mediaStream = null;

    isTranscriptionActive = false;
  };

  const changeAudioDevice = async (selectedAudioDevice?: string) => {
    source?.disconnect();
    mediaStream?.getTracks()?.forEach(track => {
      // eslint-disable-next-line no-param-reassign
      (track as any).onended = null;
      track.stop();
    });

    source = undefined;
    mediaStream = null;

    const audioOptions = {
      sampleRate: SAMPLE_RATE,
      channelCount: 1,
    };
    if (selectedAudioDevice) {
      // @ts-ignore
      audioOptions.deviceId = { ideal: selectedAudioDevice };
    }
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: audioOptions,
    });

    currentDeviceId = selectedAudioDevice;
    mediaStream.getTracks()[0].onended = handleDeviceChange;

    source = audioContext?.createMediaStreamSource(mediaStream);
    if (worklet) source?.connect(worklet);
  };

  const startTranscription = async (selectedAudioDevice?: string) => {
    if (!adviceRoomId) return;

    clearInterval(healthCheckInterval);
    healthCheckInterval = setInterval(checkHealth, 1000);

    try {
      await connectWebsocket();

      if (!audioContext || !worklet) {
        if (isFirefox) {
          audioContext = new AudioContext();
        } else {
          audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
        }

        await audioContext.audioWorklet.addModule('./pcm-processor.js');

        audioContext.onstatechange = () => {
          if (audioContext?.state === 'suspended' && isTranscriptionActive) {
            audioContext?.resume();
          }
        };
      }

      if (!worklet) {
        worklet = new AudioWorkletNode(audioContext, 'pcm-processor');
        worklet?.port.addEventListener('message', handleWorkletMessage);

        worklet.onprocessorerror = () => {
          window.Rollbar?.error('Some error occured while sending worklet message', {});
        };

        worklet.port.start();
      }
    } catch (err) {
      window.Rollbar?.error('Some error occured while starting transcriptions', {});
      showErrorToast('Some error occured');
    }

    await changeAudioDevice(selectedAudioDevice);
    isTranscriptionActive = true;
  };

  return {
    initTranscribeHandler,
    startTranscription,
    stopTranscription,
    changeAudioDevice,
    cleanUpTranscription,
  };
}
